/// <amd-module name="VSS/Features/ReduxDirectory/Redux/Artifacts/ArtifactReducer" />
import { IDirectoryArtifact } from "VSS/Features/Directory/DirectoryContracts";
import { DirectoryActions, DirectoryActionTypes } from "VSS/Features/ReduxDirectory/Redux/DirectoryActions";
import { FavoriteActions, FavoriteActionTypes } from "VSS/Features/ReduxDirectory/Redux/Favorites/FavoriteActions";
import { toLookup } from "VSS/Platform/Util/Array";
import produce from "immer";
import { IDirectoryArtifactState } from "Contracts";

/**
 * Reducer which handles actions related to the directory artifacts
 */
export function artifactReducer<T extends IDirectoryArtifact>(
    state: IDirectoryArtifactState<T>,
    action: DirectoryActions | FavoriteActions
): IDirectoryArtifactState<T> {
    state = state || getDefaultState();

    return produce(state, draft => {
        switch (action.type) {
            case DirectoryActionTypes.InitialDataLoaded: {
                draft.artifacts = { ...state.artifacts, ...toLookup(action.payload.artifacts as T[], a => a.id) };
                draft.customKeyGroupings = action.payload.customGroupings as { [key: string]: string[] };
                draft.recentArtifactKeys = action.payload.recentKeys;
                draft.readyToMarkTti = true;
                if (action.payload.favorites) {
                    for (const favorite of action.payload.favorites) {
                        if (favorite.isDeleted) {
                            // create a dummy artifact. Cast as any, because we cannot know the type of T
                            draft.deletedArtifacts[favorite.artifactId] = {
                                id: favorite.artifactId,
                                name: favorite.artifactName
                            } as any;
                        }
                    }
                }
                break;
            }

            case DirectoryActionTypes.LoadAllDataSucceeded: {
                draft.artifacts = { ...state.artifacts, ...toLookup(action.payload.artifacts as T[], a => a.id) };
                break;
            }

            case DirectoryActionTypes.ArtifactUpdated: {
                const { artifact, artifactId } = action.payload;
                draft.artifacts[artifactId] = artifact as T;

                break;
            }

            case DirectoryActionTypes.RemoveArtifact: {
                const { artifactId } = action.payload;

                delete draft.artifacts[artifactId];
                delete draft.deletedArtifacts[artifactId];

                for (const group in state.customKeyGroupings) {
                    const index = state.customKeyGroupings[group].indexOf(artifactId);
                    if (index >= 0) {
                        draft.customKeyGroupings[group].splice(index, 1);
                    }
                }

                const recentIndex = state.recentArtifactKeys.indexOf(artifactId);
                if (recentIndex >= 0) {
                    draft.recentArtifactKeys.splice(recentIndex, 1);
                }

                break;
            }

            case FavoriteActionTypes.RemoveFavoriteSuccessful: {
                const artifactId = action.payload;
                if (state.deletedArtifacts[artifactId]) {
                    delete draft.deletedArtifacts[artifactId];
                }

                break;
            }
        }
    });
}

export function getDefaultState<T extends IDirectoryArtifact>(): IDirectoryArtifactState<T> {
    return {
        artifacts: {},
        deletedArtifacts: {},
        customKeyGroupings: {},
        recentArtifactKeys: [],
        readyToMarkTti: false
    };
}
